import { useFeatureFlag } from '@src/hooks';
import {
  agGridMenuSeparator,
  filterByCellValueMenuItem,
  useCallbackRef,
  useMixpanel,
  type AgGridMenuItems,
  type CareOrder,
  type FilterableProperty,
  type UseFilterBuilderOutput,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, IRowNode } from 'ag-grid-community';
import { compact } from 'lodash-es';
import type { ReactNode } from 'react';
import { useCareOrderActionMenuItems } from '../MenuItems/useCareOrderActionMenuItems';
import { useModifyNettingMenuItem } from '../MenuItems/useModifyNettingMenuItem';
import type { useNetCareOrdersMenuItem } from '../MenuItems/useNetCareOrdersMenuItem';
import { colIDToFilterBuilderKey } from './useCareOrderManagementFilters';

interface UseCareOrderManagementMenuParams {
  filterableProperties: FilterableProperty[];
  openClause: UseFilterBuilderOutput['addAndOpenClause'];
  netCareOrdersMenu: ReturnType<typeof useNetCareOrdersMenuItem<CareOrder>>;
}

export function useCareOrderManagementMenu({
  filterableProperties,
  openClause,
  netCareOrdersMenu,
}: UseCareOrderManagementMenuParams): {
  getCustomContextMenuItems: (params: GetContextMenuItemsParams<CareOrder>) => AgGridMenuItems;
  dialogs: ReactNode;
} {
  const mixpanel = useMixpanel();
  const modifyNettingMenu = useModifyNettingMenuItem<CareOrder>();
  const careOrderActionMenuItems = useCareOrderActionMenuItems();
  const { enableCareOrderNetting } = useFeatureFlag();

  const getCustomContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<CareOrder>) => {
    const entity = params.node?.data;
    const selectedCareOrders = getSelectedCareOrderRows(params);
    const modifyItem = entity ? careOrderActionMenuItems.getModifyMenuItem(entity) : null;
    const approveComplianceItem = entity ? careOrderActionMenuItems.getApproveComplianceMenuItem(entity) : null;
    const cancelItem = entity
      ? careOrderActionMenuItems.getCancelMenuItem({
          entity,
          isCancelable: entity.isCancelable,
        })
      : null;
    const forceCancelItem = entity ? careOrderActionMenuItems.getForceCancelMenuItem(entity) : null;

    const netCareOrdersItem = enableCareOrderNetting
      ? netCareOrdersMenu.getMenuItem({
          selectedCareOrders,
        })
      : null;
    const modifyNettingItem = enableCareOrderNetting
      ? modifyNettingMenu.getMenuItem({
          contextCareOrder: entity,
          selectedCareOrders,
        })
      : null;

    const hasActionItems = !!(approveComplianceItem || modifyItem || cancelItem || forceCancelItem);

    return compact([
      ...filterByCellValueMenuItem({
        params,
        filterableProperties,
        openClause,
        colIDToFilterBuilderKey,
        mixpanel,
      }),
      modifyNettingItem,
      netCareOrdersItem,
      hasActionItems ? agGridMenuSeparator : null,
      approveComplianceItem,
      modifyItem,
      cancelItem,
      forceCancelItem,
    ]);
  });

  return {
    getCustomContextMenuItems,
    dialogs: (
      <>
        {netCareOrdersMenu.dialogs}
        {modifyNettingMenu.dialogs}
      </>
    ),
  };
}

function getSelectedCareOrderRows(params: GetContextMenuItemsParams<CareOrder>): CareOrder[] {
  return getSelectedCareOrderRowsFromNodes(params.api.getSelectedNodes());
}

export function getSelectedCareOrderRowsFromNodes(nodes: IRowNode<CareOrder>[]): CareOrder[] {
  const isCareOrderRowNode = (node: IRowNode<CareOrder>): node is IRowNode<CareOrder> & { data: CareOrder } =>
    node.data != null && !node.data.isNetted;

  return nodes.filter(isCareOrderRowNode).map(node => node.data);
}
